import { useState, useEffect, useRef } from "react";
import { getHoursCloseTicketsAuto } from "../../config";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const useTickets = ({
    searchParam,
    pageNumber,
    status,
    date,
    showAll,
    queueIds,
    withUnreadMessages,
    refreshTrigger,
}) => {
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [count, setCount] = useState(0);
    const lastAutoCloseRunRef = useRef(0);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const closeStaleTickets = async (ticketsList) => {
                const autoCloseHours = Number(getHoursCloseTicketsAuto());
                const oneMinute = 60 * 1000;
                const now = Date.now();

                if (
                    status !== "open" ||
                    pageNumber !== 1 ||
                    searchParam ||
                    !autoCloseHours ||
                    autoCloseHours <= 0 ||
                    now - lastAutoCloseRunRef.current < oneMinute
                ) {
                    return;
                }

                lastAutoCloseRunRef.current = now;

                const limitDate = new Date(now - autoCloseHours * 60 * 60 * 1000);
                const staleTickets = ticketsList
                    .filter(ticket => ticket.status !== "closed")
                    .filter(ticket => new Date(ticket.updatedAt) < limitDate)
                    .slice(0, 25);

                if (staleTickets.length === 0) {
                    return;
                }

                await Promise.allSettled(
                    staleTickets.map(ticket =>
                        api.put(`/tickets/${ticket.id}`, {
                            status: "closed",
                            userId: ticket.userId || null
                        })
                    )
                );
            };

            const fetchTickets = async() => {
                try {
                    const { data } = await api.get("/tickets", {
                        params: {
                            searchParam,
                            pageNumber,
                            status,
                            date,
                            showAll,
                            queueIds,
                            withUnreadMessages,
                        },
                    })
                    setTickets(data.tickets)
                    closeStaleTickets(data.tickets).catch(() => undefined);

                    setHasMore(data.hasMore)
                    setCount(data.count)
                    setLoading(false)
                } catch (err) {
                    setLoading(false)
                    toastError(err)
                }
            }

            fetchTickets()
        }, 120)
        return () => clearTimeout(delayDebounceFn)
    }, [
        searchParam,
        pageNumber,
        status,
        date,
        showAll,
        queueIds,
        withUnreadMessages,
        refreshTrigger,
    ])

    return { tickets, loading, hasMore, count };
};

export default useTickets;
